export const NO_RECORDING_REASON = "This call was not recorded, so there is nothing to transcribe.";

export type AiInsightsRequest = "summary" | "transcript";

export interface AiInsightsAction {
    label: string;
    request: AiInsightsRequest;
}

export interface AiInsightsState {
    /** An artifact already exists, so the insights page has something to show. */
    canView: boolean;
    /** Both artifacts exist — nothing left to generate. */
    isComplete: boolean;
    /** What can still be generated, when anything can. */
    action: AiInsightsAction | null;
    /** Why nothing can be offered, said only when there is also nothing to view. */
    unavailableReason: string | null;
}

interface AiInsightsAvailability {
    readonly hasTranscript: boolean;
    readonly hasSummary: boolean;
    /**
     * Whether the call has audio to work from. A transcript needs it; a summary
     * does not — tenants can have summaries produced for calls that were never
     * recorded, so this must not gate the summary side.
     */
    readonly hasRecording: boolean;
}

export const resolveAiInsightsState = ({
    hasTranscript,
    hasSummary,
    hasRecording,
}: AiInsightsAvailability): AiInsightsState => {
    const canView = hasTranscript || hasSummary;

    if (hasTranscript && hasSummary) {
        return {canView, isComplete: true, action: null, unavailableReason: null};
    }

    if (hasTranscript) {
        // A summary is written from the transcript, so it stays available even
        // for a call whose recording is gone or was never made.
        return {
            canView,
            isComplete: false,
            action: {label: "Generate summary", request: "summary"},
            unavailableReason: null,
        };
    }

    // Transcription is the only thing that genuinely needs audio. Without a
    // recording the action cannot succeed, so it is not offered — but an
    // existing summary is still worth opening.
    if (!hasRecording) {
        return {
            canView,
            isComplete: false,
            action: null,
            unavailableReason: canView ? null : NO_RECORDING_REASON,
        };
    }

    if (hasSummary) {
        return {
            canView,
            isComplete: false,
            action: {label: "Generate transcript", request: "transcript"},
            unavailableReason: null,
        };
    }

    return {
        canView,
        isComplete: false,
        action: {label: "Generate AI Insights", request: "summary"},
        unavailableReason: null,
    };
};
